// DXF export of the construction detail: ASCII DXF R12 (AC1009, the most compatible: AutoCAD, BricsCAD, DraftSight,
// LibreCAD, Rhino...), mm, real scale 1:1. Content: (1) FRONT VIEW of the elements (element 2 vertical, element 1
// rotated by θ, screws in plan, dimensions b/d/s1/s2/a2, angle θ); (2) each element in its own plane with the edge
// distances (as the "Posizionamento viti" card); (3) title block with project/screw/verification data and the position
// table. Admissible screws = circle of the real diameter + axis cross; dimensions are real DIMENSION entities (the text
// is the measure recomputed by the CAD). Layers: ELEMENTO_1, ELEMENTO_2, VITI, VITI_3, ASSI, QUOTE, TESTI, TABELLA.
// Screws outside the envelope are not drawn. Available only for a verified connection (dxfAvailability).
import { gridAxis2 } from "../../../http/screwConnector/formulas/grid";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import { formatReportDate, type ReportContext } from "./reportContext";
import { fmtN } from "./reportFormulas";
import { posFmt } from "./positionHtml";

// Title of the connection type (the HTML's #materiale1 option text), capitalised as the HTML's updateCrumb.
function connectionTitle(type: string, ctx: ReportContext): string {
  const text = ctx.texts.typeSection.types[type as keyof ReportContext["texts"]["typeSection"]["types"]] ?? type;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

type Pt = [number, number];
type TextOpts = { align?: "l" | "c" | "r"; valign?: "b" | "bot" | "m"; rot?: number };

export function dxfEscape(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/[\r\n]+/g, " ")
    .replace(/[Øø]/g, "d")
    .replace(/°/g, " gradi")
    .replace(/[–—−]/g, "-")
    .replace(/[²]/g, "2")
    .replace(/[×]/g, "x")
    .replace(/θ/g, "theta")
    .replace(/η/g, "eta")
    .replace(/γ/g, "gamma")
    .replace(/ρ/g, "rho")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\x20-\x7E]/g, "?");
}

export function dxfBuilder(th: number) {
  const E: string[] = [],
    B: string[] = [];
  let nDim = 0;
  const n = (v: number) => String(Math.round(v * 1000) / 1000);
  const e = (arr: string[], code: number, val: string | number) => {
    arr.push(String(code), String(val));
  };
  const line = (arr: string[], layer: string, x1: number, y1: number, x2: number, y2: number, lt?: string) => {
    e(arr, 0, "LINE");
    e(arr, 8, layer);
    if (lt) e(arr, 6, lt);
    e(arr, 10, n(x1));
    e(arr, 20, n(y1));
    e(arr, 30, 0);
    e(arr, 11, n(x2));
    e(arr, 21, n(y2));
    e(arr, 31, 0);
  };
  const circle = (arr: string[], layer: string, x: number, y: number, r: number) => {
    e(arr, 0, "CIRCLE");
    e(arr, 8, layer);
    e(arr, 10, n(x));
    e(arr, 20, n(y));
    e(arr, 30, 0);
    e(arr, 40, n(r));
  };
  const arc = (arr: string[], layer: string, x: number, y: number, r: number, a0: number, a1: number) => {
    e(arr, 0, "ARC");
    e(arr, 8, layer);
    e(arr, 10, n(x));
    e(arr, 20, n(y));
    e(arr, 30, 0);
    e(arr, 40, n(r));
    e(arr, 50, n(a0));
    e(arr, 51, n(a1));
  };
  // align: l | c | r; valign: b (baseline) | bot (bottom) | m (middle); rot in degrees
  const text = (arr: string[], layer: string, x: number, y: number, h: number, str: string, o?: TextOpts) => {
    const opts = o || {};
    const hA = opts.align === "c" ? 1 : opts.align === "r" ? 2 : 0;
    const vA = opts.valign === "m" ? 2 : opts.valign === "bot" ? 1 : 0;
    e(arr, 0, "TEXT");
    e(arr, 8, layer);
    e(arr, 10, n(x));
    e(arr, 20, n(y));
    e(arr, 30, 0);
    e(arr, 40, n(h));
    e(arr, 1, dxfEscape(str));
    if (opts.rot) e(arr, 50, n(opts.rot));
    e(arr, 7, "STANDARD");
    if (hA || vA) {
      e(arr, 72, hA);
      e(arr, 73, vA);
      e(arr, 11, n(x));
      e(arr, 21, n(y));
      e(arr, 31, 0);
    }
  };
  const polyline = (arr: string[], layer: string, pts: Pt[], closed: boolean) => {
    e(arr, 0, "POLYLINE");
    e(arr, 8, layer);
    e(arr, 66, 1);
    e(arr, 10, 0);
    e(arr, 20, 0);
    e(arr, 30, 0);
    e(arr, 70, closed ? 1 : 0);
    pts.forEach((p) => {
      e(arr, 0, "VERTEX");
      e(arr, 8, layer);
      e(arr, 10, n(p[0]));
      e(arr, 20, n(p[1]));
      e(arr, 30, 0);
    });
    e(arr, 0, "SEQEND");
    e(arr, 8, layer);
  };
  const ts = 0.35 * th,
    exo = 0.3 * th,
    exe = 0.6 * th,
    gap = 0.3 * th;
  // Rotated linear dimension (alphaDeg = direction of the dimension line; 0 horizontal, 90 vertical). p1/p2 = origins
  // of the extension lines; tq = position of the dimension line as n·Q with n = (−sin α, cos α); prefix = text before
  // the measure (e.g. "b1="). The text is the measure recomputed by the CAD ("<>").
  const dimR = (alphaDeg: number, p1: Pt, p2: Pt, tq: number, prefix?: string) => {
    nDim++;
    const name = "*D" + nDim,
      L = "QUOTE";
    const al = (alphaDeg * Math.PI) / 180,
      a: Pt = [Math.cos(al), Math.sin(al)],
      nn: Pt = [-Math.sin(al), Math.cos(al)];
    const dot = (u: Pt, w: Pt) => u[0] * w[0] + u[1] * w[1];
    const foot = (p: Pt): [number, number, number] => {
      const t = tq - dot(nn, p);
      return [p[0] + nn[0] * t, p[1] + nn[1] * t, t];
    };
    const f1 = foot(p1),
      f2 = foot(p2);
    const meas = Math.abs(dot(a, [p2[0] - p1[0], p2[1] - p1[1]]));
    const label = (prefix || "") + String(Math.round(meas));
    const an = ((alphaDeg % 360) + 360) % 360;
    const ta = an > 90 && an <= 270 ? alphaDeg + 180 : alphaDeg,
      tr = (ta * Math.PI) / 180;
    const tn: Pt = [-Math.sin(tr), Math.cos(tr)];
    const mid: Pt = [(f1[0] + f2[0]) / 2, (f1[1] + f2[1]) / 2];
    // anonymous block with the dimension geometry (read by programs that do not regenerate dimensions)
    e(B, 0, "BLOCK");
    e(B, 8, 0);
    e(B, 2, name);
    e(B, 70, 1);
    e(B, 10, 0);
    e(B, 20, 0);
    e(B, 30, 0);
    e(B, 3, name);
    (
      [
        [p1, f1],
        [p2, f2],
      ] as [Pt, [number, number, number]][]
    ).forEach(([p, f]) => {
      const t = f[2];
      if (Math.abs(t) > exo + 1e-6) {
        const s = t >= 0 ? 1 : -1;
        line(B, L, p[0] + nn[0] * s * exo, p[1] + nn[1] * s * exo, f[0] + nn[0] * s * exe, f[1] + nn[1] * s * exe);
      }
    });
    line(B, L, f1[0], f1[1], f2[0], f2[1]);
    const dd: Pt = [(a[0] + nn[0]) / Math.SQRT2, (a[1] + nn[1]) / Math.SQRT2];
    [f1, f2].forEach((f) => line(B, L, f[0] - dd[0] * ts, f[1] - dd[1] * ts, f[0] + dd[0] * ts, f[1] + dd[1] * ts));
    text(B, L, mid[0] + tn[0] * gap, mid[1] + tn[1] * gap, th, label, { align: "c", valign: "bot", rot: ta });
    e(B, 0, "ENDBLK");
    e(B, 8, 0);
    // DIMENSION entity
    e(E, 0, "DIMENSION");
    e(E, 8, L);
    e(E, 2, name);
    e(E, 10, n(f2[0]));
    e(E, 20, n(f2[1]));
    e(E, 30, 0);
    e(E, 11, n(mid[0] + tn[0] * (gap + th / 2)));
    e(E, 21, n(mid[1] + tn[1] * (gap + th / 2)));
    e(E, 31, 0);
    e(E, 70, 0);
    if (prefix) e(E, 1, dxfEscape(prefix) + "<>");
    e(E, 13, n(p1[0]));
    e(E, 23, n(p1[1]));
    e(E, 33, 0);
    e(E, 14, n(p2[0]));
    e(E, 24, n(p2[1]));
    e(E, 34, 0);
    if (Math.abs(alphaDeg) > 1e-9) e(E, 50, n(alphaDeg));
  };
  const dim = (kind: "h" | "v", p1: Pt, p2: Pt, pos: number, prefix?: string) =>
    kind === "h" ? dimR(0, p1, p2, pos, prefix) : dimR(90, p1, p2, -pos, prefix);
  const finish = (bbox: [number, number, number, number]) => {
    const H: string[] = [];
    e(H, 0, "SECTION");
    e(H, 2, "HEADER");
    e(H, 9, "$ACADVER");
    e(H, 1, "AC1009");
    e(H, 9, "$INSBASE");
    e(H, 10, 0);
    e(H, 20, 0);
    e(H, 30, 0);
    e(H, 9, "$EXTMIN");
    e(H, 10, n(bbox[0]));
    e(H, 20, n(bbox[1]));
    e(H, 30, 0);
    e(H, 9, "$EXTMAX");
    e(H, 10, n(bbox[2]));
    e(H, 20, n(bbox[3]));
    e(H, 30, 0);
    e(H, 9, "$LTSCALE");
    e(H, 40, 1);
    e(H, 9, "$LUNITS");
    e(H, 70, 2);
    e(H, 9, "$LUPREC");
    e(H, 70, 0);
    e(H, 9, "$TEXTSTYLE");
    e(H, 7, "STANDARD");
    e(H, 9, "$TEXTSIZE");
    e(H, 40, n(th));
    e(H, 9, "$DIMSCALE");
    e(H, 40, 1);
    e(H, 9, "$DIMTXT");
    e(H, 40, n(th));
    e(H, 9, "$DIMTSZ");
    e(H, 40, n(ts));
    e(H, 9, "$DIMASZ");
    e(H, 40, n(th * 0.5));
    e(H, 9, "$DIMEXO");
    e(H, 40, n(exo));
    e(H, 9, "$DIMEXE");
    e(H, 40, n(exe));
    e(H, 9, "$DIMGAP");
    e(H, 40, n(gap));
    e(H, 9, "$DIMTAD");
    e(H, 70, 1);
    e(H, 9, "$DIMTIH");
    e(H, 70, 0);
    e(H, 9, "$DIMTOH");
    e(H, 70, 0);
    e(H, 9, "$DIMCLRD");
    e(H, 70, 4);
    e(H, 9, "$DIMCLRE");
    e(H, 70, 4);
    e(H, 9, "$DIMCLRT");
    e(H, 70, 4);
    e(H, 0, "ENDSEC");
    // tables
    e(H, 0, "SECTION");
    e(H, 2, "TABLES");
    e(H, 0, "TABLE");
    e(H, 2, "LTYPE");
    e(H, 70, 2);
    e(H, 0, "LTYPE");
    e(H, 2, "CONTINUOUS");
    e(H, 70, 0);
    e(H, 3, "Solid line");
    e(H, 72, 65);
    e(H, 73, 0);
    e(H, 40, 0);
    e(H, 0, "LTYPE");
    e(H, 2, "CENTER");
    e(H, 70, 0);
    e(H, 3, "Center ____ _ ____ _ ____");
    e(H, 72, 65);
    e(H, 73, 4);
    e(H, 40, n(th * 6));
    e(H, 49, n(th * 4));
    e(H, 49, n(-th));
    e(H, 49, n(th * 0.5));
    e(H, 49, n(-th * 0.5));
    e(H, 0, "ENDTAB");
    const layers: [string, number, string][] = [
      ["0", 7, "CONTINUOUS"],
      ["ELEMENTO_1", 7, "CONTINUOUS"],
      ["ELEMENTO_2", 7, "CONTINUOUS"],
      ["VITI", 5, "CONTINUOUS"],
      ["VITI_3", 3, "CONTINUOUS"],
      ["ASSI", 1, "CENTER"],
      ["QUOTE", 4, "CONTINUOUS"],
      ["TESTI", 7, "CONTINUOUS"],
      ["TABELLA", 8, "CONTINUOUS"],
    ];
    e(H, 0, "TABLE");
    e(H, 2, "LAYER");
    e(H, 70, layers.length);
    layers.forEach((l) => {
      e(H, 0, "LAYER");
      e(H, 2, l[0]);
      e(H, 70, 0);
      e(H, 62, l[1]);
      e(H, 6, l[2]);
    });
    e(H, 0, "ENDTAB");
    e(H, 0, "TABLE");
    e(H, 2, "STYLE");
    e(H, 70, 1);
    e(H, 0, "STYLE");
    e(H, 2, "STANDARD");
    e(H, 70, 0);
    e(H, 40, 0);
    e(H, 41, 1);
    e(H, 50, 0);
    e(H, 71, 0);
    e(H, 42, n(th));
    e(H, 3, "txt");
    e(H, 4, "");
    e(H, 0, "ENDTAB");
    e(H, 0, "ENDSEC");
    e(H, 0, "SECTION");
    e(H, 2, "BLOCKS");
    const all = H.concat(B);
    e(all, 0, "ENDSEC");
    e(all, 0, "SECTION");
    e(all, 2, "ENTITIES");
    const res = all.concat(E);
    e(res, 0, "ENDSEC");
    e(res, 0, "EOF");
    return res.join("\r\n") + "\r\n";
  };
  return { E, line, circle, arc, text, polyline, dim, dimR, finish };
}

export type DxfAvailability = { ok: boolean; reason: string };

// Export availability: calculation run and connection verified (every check ≤ 100 %, every screw in the envelope).
export function dxfAvailability(state: CalcState | null, blockReason?: string | null): DxfAvailability {
  if (!state || blockReason || state.blocked) return { ok: false, reason: "Disponibile dopo il calcolo (compilare i dati di progetto)." };
  if (!state.screwRec) return { ok: false, reason: "Selezionare una vite per esportare il dettaglio." };
  const verified =
    Number.isFinite(state.eta) &&
    state.eta <= 1 &&
    state.viteAmmissibili === state.viteTotali &&
    (state.etaCombinata == null || state.etaCombinata <= 1) &&
    (state.etaAxSimple == null || state.etaAxSimple <= 1) &&
    (!(state.three || state.steel3) || state.etaSplitting == null || state.etaSplitting <= 1) &&
    (!state.gridB || state.gridB.every((p) => p.ok));
  return verified
    ? {
        ok: true,
        reason: "Esporta il dettaglio costruttivo (vista frontale, posizionamento viti, quote in mm) in formato DXF.",
      }
    : {
        ok: false,
        reason: "Disponibile solo con connessione verificata (tutte le verifiche ≤ 100 % e tutte le viti ammissibili).",
      };
}

export function buildDxf(state: CalcState, ctx: ReportContext): string {
  const grid = state.grid.filter((p) => p.ok);
  const d = state.d;
  const th = Math.max(3, Math.round(Math.max(state.b1, state.b2) * 0.03));
  const D = dxfBuilder(th);
  const r = d / 2;
  const cont1 = !!(state.lines1 && state.lines1.continuo1),
    cont2 = !!(state.bounds2 && state.bounds2.continuo2);
  const isSteel = state.materiale1 === "acciaio";
  const showNumbers = grid.length <= 24;
  const three = !!state.three;
  const steel3 = !!state.steel3;
  // 3 elements: screws of side 3 (mirror of side 2), drawn separately (layer VITI_3) only when staggered
  const gridB3 = (three || steel3) && state.stagger && state.gridB ? state.gridB.filter((p) => p.ok) : [];
  type PlanPt = { h: number; v: number; k?: number };
  const pts1: PlanPt[] = grid.map((p, i) => ({ h: p.v, v: p.u, k: i + 1 }));
  const pts2: PlanPt[] = grid.map((p, i) => ({ h: p.x, v: p.y, k: i + 1 }));
  const ptsB1: PlanPt[] = gridB3.map((p) => ({ h: p.v, v: p.u }));
  const ptsB2: PlanPt[] = gridB3.map((p) => ({ h: p.x, v: p.y }));
  const drawScrew = (cx: number, cy: number, k: number | null | undefined, layer?: string) => {
    const cr = 1.6 * r,
      L0 = layer || "VITI";
    D.circle(D.E, L0, cx, cy, r);
    D.line(D.E, L0, cx - cr, cy, cx + cr, cy);
    D.line(D.E, L0, cx, cy - cr, cx, cy + cr);
    if (showNumbers && k != null) D.text(D.E, "TESTI", cx + cr * 1.1, cy + cr * 1.1, th * 0.6, String(k));
  };
  // Open side (continuing element): approximated with a broken cut line, without dimensions.
  const brk = (layer: string, a: Pt, b: Pt, nrm: Pt) => {
    const pts: Pt[] = [];
    for (let i = 0; i <= 6; i++) {
      const f = i / 6,
        amp = i === 0 || i === 6 ? 0 : (i % 2 ? 1 : -1) * 0.5 * th;
      pts.push([a[0] + (b[0] - a[0]) * f + nrm[0] * amp, a[1] + (b[1] - a[1]) * f + nrm[1] * amp]);
    }
    D.polyline(D.E, layer, pts, false);
  };
  const titleAt = (x: number, ytop: number, title: string, sub: string[]) => {
    D.text(D.E, "TESTI", x, ytop + (1.0 + 1.2 * sub.length) * th, th * 1.1, title);
    sub.forEach((t, i) => D.text(D.E, "TESTI", x, ytop + (1.0 + 1.2 * (sub.length - 1 - i)) * th, th * 0.7, t));
  };
  // ---- FRONT VIEW (projection along the screw axis): element 2 vertical, element 1 rotated by θ; the "infinite"
  // sides are cut as in the 3D view (open outline).
  const planFront = () => {
    const D2R = Math.PI / 180,
      s = Math.sin(state.theta * D2R),
      c = Math.cos(state.theta * D2R);
    const b1 = state.b1,
      b2 = state.b2;
    const all = state.grid,
      gm = Math.max(d * 3, 30);
    const gU = all.map((p) => p.u),
      gY = all.map((p) => p.y);
    // a continuous element has no length (its d1/d2 field is hidden and may be empty)
    const yTopBase = Math.max(cont2 ? 0 : state.d2, b1, b2) * 0.9,
      yTop = Math.max(yTopBase, Math.max(...gY) + gm);
    const yBot = cont2 ? -Math.max(yTopBase, -Math.min(...gY) + gm) : -state.d2;
    const uFarBase = Math.max(b2 * 0.8, cont1 ? 0 : state.d1 * 0.45),
      uFar = Math.max(uFarBase, Math.max(...gU) + gm);
    const uNear = cont1 ? -Math.max(uFarBase, -Math.min(...gU) + gm) : -state.d1;
    const XY = (u: number, v: number): Pt => [-u * s + v * c, u * c + v * s];
    const off = 3.4 * th;
    const corners: Pt[] = [
      [-b2 / 2, yTop],
      [b2 / 2, yTop],
      [-b2 / 2, yBot],
      [b2 / 2, yBot],
      XY(uFar, -b1 / 2),
      XY(uFar, b1 / 2),
      XY(uNear, -b1 / 2),
      XY(uNear, b1 / 2),
      XY(uNear - off, -b1 / 2),
      XY(uNear - off, b1 / 2),
      XY(uNear, -b1 / 2 - off),
      [b2 / 2 + off, yBot - off],
    ];
    const xs = corners.map((p) => p[0]),
      ys = corners.map((p) => p[1]);
    const minX = Math.min(...xs),
      maxX = Math.max(...xs),
      minY = Math.min(...ys) - 2 * th,
      maxY = Math.max(...ys);
    const sub = three
      ? [
          "elementi visti lungo l'asse delle viti",
          "elementi 2 e 3 (identici) sovrapposti in vista, elemento 1 centrale ruotato di theta",
          state.threeD
            ? "viti passanti nei tre elementi - quote in mm"
            : "viti del lato 2 (blu) e del lato 3 (verde, se sfalsate) - quote in mm",
        ]
      : steel3
        ? [
            "elementi visti lungo l'asse delle viti",
            (state.panel1 ? "pannelli 1 e 3 (identici)" : "lamiere 1 e 3 (identiche)") +
              " ruotati di theta sovrapposti in vista, elemento 2 in legno centrale",
            "viti del lato 1 (blu) e del lato 3 (verde, se sfalsate) - quote in mm",
          ]
        : [
            "elementi 1 e 2 visti lungo l'asse delle viti",
            "elemento 2 verticale, elemento 1 ruotato di theta",
            "viti ammissibili in pianta - quote in mm",
          ];
    return {
      left: -minX + 2 * th,
      right: maxX + 2.5 * th,
      up: maxY + (2.8 + 1.2 * sub.length) * th,
      down: minY - 1.5 * th,
      draw(ox: number, oy: number) {
        const X = (x: number) => x + ox,
          Y = (y: number) => y + oy;
        const P = (p: Pt): Pt => [X(p[0]), Y(p[1])];
        // element 2
        if (cont2) {
          D.polyline(
            D.E,
            "ELEMENTO_2",
            [
              [X(-b2 / 2), Y(yTop)],
              [X(-b2 / 2), Y(yBot)],
            ],
            false,
          );
          D.polyline(
            D.E,
            "ELEMENTO_2",
            [
              [X(b2 / 2), Y(yTop)],
              [X(b2 / 2), Y(yBot)],
            ],
            false,
          );
        } else
          D.polyline(
            D.E,
            "ELEMENTO_2",
            [
              [X(-b2 / 2), Y(yTop)],
              [X(-b2 / 2), Y(yBot)],
              [X(b2 / 2), Y(yBot)],
              [X(b2 / 2), Y(yTop)],
            ],
            false,
          );
        // element 1 (rotated)
        const A = P(XY(uFar, -b1 / 2)),
          Bn = P(XY(uNear, -b1 / 2)),
          Cn = P(XY(uNear, b1 / 2)),
          Dn = P(XY(uFar, b1 / 2));
        if (cont1) {
          D.polyline(D.E, "ELEMENTO_1", [A, Bn], false);
          D.polyline(D.E, "ELEMENTO_1", [Cn, Dn], false);
        } else D.polyline(D.E, "ELEMENTO_1", [A, Bn, Cn, Dn], false);
        // open sides: cut line without dimensions
        brk("ELEMENTO_2", [X(-b2 / 2), Y(yTop)], [X(b2 / 2), Y(yTop)], [0, 1]);
        if (cont2) brk("ELEMENTO_2", [X(-b2 / 2), Y(yBot)], [X(b2 / 2), Y(yBot)], [0, 1]);
        brk("ELEMENTO_1", A, Dn, [-s, c]);
        if (cont1) brk("ELEMENTO_1", Bn, Cn, [-s, c]);
        // axes: element 2 (x = 0) and element 1 (u axis), crossing at the origin
        D.line(D.E, "ASSI", X(0), Y(yTop + th), X(0), Y(yBot - th), "CENTER");
        const a0 = XY(uFar + th, 0),
          a1 = XY(uNear - th, 0);
        D.line(D.E, "ASSI", X(a0[0]), Y(a0[1]), X(a1[0]), Y(a1[1]), "CENTER");
        // admissible screws
        grid.forEach((p, i) => drawScrew(X(p.x), Y(p.y), i + 1));
        gridB3.forEach((p) => drawScrew(X(p.x), Y(p.y), null, "VITI_3"));
        // dimensions (local coordinates + view offset; tqLocal = n·Q with n = (−sin α, cos α))
        const dimL = (alpha: number, pa: Pt, pb: Pt, tqLocal: number, prefix?: string) => {
          const al = alpha * D2R,
            nn: Pt = [-Math.sin(al), Math.cos(al)];
          D.dimR(alpha, P(pa), P(pb), tqLocal + nn[0] * ox + nn[1] * oy, prefix);
        };
        dimL(0, [-b2 / 2, yBot], [b2 / 2, yBot], yBot - off, "b2=");
        if (!cont2) dimL(90, [b2 / 2, yBot], [b2 / 2, 0], -(b2 / 2 + off), "d2=");
        dimL(state.theta, XY(uNear, -b1 / 2), XY(uNear, b1 / 2), uNear - off, "b1=");
        if (!cont1) dimL(state.theta + 90, XY(-state.d1, -b1 / 2), XY(0, -b1 / 2), b1 / 2 + off, "d1=");
        const colJ0 = grid.filter((p) => p.j === 0 && (p.i === 0 || p.i === 1)).sort((a, b) => a.i - b.i);
        const rowI0 = grid.filter((p) => p.i === 0 && (p.j === 0 || p.j === 1)).sort((a, b) => a.j - b.j);
        const ang = (dx: number, dy: number) => Math.atan2(dy, dx) / D2R;
        type GP = (typeof grid)[number];
        // dimension aligned to direction alpha between two screws, outside the grid (on the side nearer to the two
        // screws) at "side" text heights from it: small dimensions do not overlap the screws
        const dimAligned = (pa: GP, pb: GP, alpha: number, prefix: string, side: number) => {
          const al = alpha * D2R,
            nn: Pt = [-Math.sin(al), Math.cos(al)];
          const tAll = grid.map((g) => nn[0] * g.x + nn[1] * g.y),
            lo = Math.min(...tAll),
            hi = Math.max(...tAll);
          const tm = (nn[0] * (pa.x + pb.x) + nn[1] * (pa.y + pb.y)) / 2;
          dimL(alpha, [pa.x, pa.y], [pb.x, pb.y], hi - tm <= tm - lo ? hi + side * th : lo - side * th, prefix);
        };
        const c0 = colJ0[0],
          c1 = colJ0[1],
          r0 = rowI0[0],
          r1 = rowI0[1];
        if (c0 && c1) dimAligned(c0, c1, ang(c1.x - c0.x, c1.y - c0.y), "s1=", 3);
        if (r0 && r1) dimAligned(r0, r1, ang(r1.x - r0.x, r1.y - r0.y), "s2=", 3);
        // a2,1 (component of the j step perpendicular to element 1) and a2,2 (component of the i step perpendicular to
        // element 2)
        const ax2 = gridAxis2(state.theta, c, s, state.crossDominant);
        if (r0 && r1) dimAligned(r0, r1, state.theta, "a2,1=", 6);
        if (c0 && c1) dimAligned(c0, c1, ang(-ax2.ey, ax2.ex), "a2,2=", 6);
        // angle θ between the element 2 axis (downwards) and the element 1 axis (towards its end)
        const rt = Math.max(b1, b2) * 0.6;
        D.arc(D.E, "QUOTE", X(0), Y(0), rt, -90, -90 + state.theta);
        const tm = (-90 + state.theta / 2) * D2R;
        D.text(
          D.E,
          "QUOTE",
          X(Math.cos(tm) * rt * 1.08),
          Y(Math.sin(tm) * rt * 1.08),
          th,
          "theta = " + fmtN(state.theta, 0) + " gradi",
          { align: "l", valign: "m" },
        );
        titleAt(X(minX), Y(maxY), "VISTA FRONTALE", sub);
      },
    };
  };
  // ---- one element in its own plane (mirror of buildPositionSvg, y upwards)
  type PlanCfg = {
    width: number;
    endV: number;
    pts: PlanPt[];
    continuous: boolean;
    layer: string;
    title: string;
    sub: string[];
    extra?: PlanPt[];
  };
  const plan = (cfg: PlanCfg) => {
    const { width, endV, pts, continuous, layer, title, sub, extra } = cfg;
    const vMin = Math.min(...pts.map((p) => p.v)),
      vMax = Math.max(...pts.map((p) => p.v));
    const hMin = Math.min(...pts.map((p) => p.h)),
      hMax = Math.max(...pts.map((p) => p.h));
    const span = Math.max(vMax - vMin, 1),
      L = width / 2;
    // real scale 1:1, no break/compression: the DXF dimensions match the CAD measure
    const top = vMax + 0.3 * span + 0.15 * width;
    const bottom = continuous ? vMin - 0.5 * Math.max(span, width * 0.3) : endV;
    const yDimH = bottom - 3.4 * th,
      yDimTot = yDimH - 2.6 * th,
      xDimV = L + 3.4 * th;
    return {
      left: L + 2 * th,
      right: xDimV + 2 * th,
      up: top + (2.8 + 1.2 * sub.length) * th,
      down: yDimTot - 2 * th,
      draw(ox: number, oy: number) {
        const X = (x: number) => x + ox,
          YY = (y: number) => y + oy;
        if (continuous) {
          D.polyline(
            D.E,
            layer,
            [
              [X(-L), YY(top)],
              [X(-L), YY(bottom)],
            ],
            false,
          );
          D.polyline(
            D.E,
            layer,
            [
              [X(L), YY(top)],
              [X(L), YY(bottom)],
            ],
            false,
          );
        } else {
          D.polyline(
            D.E,
            layer,
            [
              [X(-L), YY(top)],
              [X(-L), YY(bottom)],
              [X(L), YY(bottom)],
              [X(L), YY(top)],
            ],
            false,
          );
        }
        brk(layer, [X(-L), YY(top)], [X(L), YY(top)], [0, 1]);
        if (continuous) brk(layer, [X(-L), YY(bottom)], [X(L), YY(bottom)], [0, 1]);
        D.line(D.E, "ASSI", X(0), YY(top + th), X(0), YY(bottom - th), "CENTER");
        pts.forEach((p) => drawScrew(X(p.h), YY(p.v), p.k));
        (extra || []).forEach((p) => drawScrew(X(p.h), YY(p.v), null, "VITI_3"));
        const pHmin = pts.find((p) => p.h === hMin) ?? pts[0]!,
          pHmax = pts.find((p) => p.h === hMax) ?? pts[0]!;
        const pVmin = pts.find((p) => p.v === vMin) ?? pts[0]!,
          pVmax = pts.find((p) => p.v === vMax) ?? pts[0]!;
        const bl: Pt = [X(-L), YY(bottom)],
          br: Pt = [X(L), YY(bottom)];
        const hm: Pt = [X(hMin), YY(pHmin.v - r)],
          hx: Pt = [X(hMax), YY(pHmax.v - r)];
        D.dim("h", bl, hm, YY(yDimH));
        if (hMax - hMin > 0.05) D.dim("h", hm, hx, YY(yDimH));
        D.dim("h", hx, br, YY(yDimH));
        D.dim("h", bl, br, YY(yDimTot));
        const vm: Pt = [X(pVmin.h + r), YY(vMin)],
          vx: Pt = [X(pVmax.h + r), YY(vMax)];
        if (!continuous) D.dim("v", br, vm, X(xDimV));
        if (vMax - vMin > 0.05) D.dim("v", vm, vx, X(xDimV));
        titleAt(X(-L), YY(top), title, sub);
      },
    };
  };
  const v0 = planFront();
  const v1 = plan({
    width: state.b1,
    endV: -state.d1,
    pts: pts1,
    extra: ptsB1,
    continuous: cont1,
    layer: "ELEMENTO_1",
    title: steel3
      ? state.panel1
        ? "ELEMENTI 1 E 3 (pannelli)"
        : "ELEMENTI 1 E 3 (lamiere)"
      : "ELEMENTO 1" + (isSteel ? " (lamiera)" : state.panel1 ? " (pannello)" : three ? " (centrale)" : ""),
    sub: ["vista nel proprio piano", "estremita in basso (opposta a F)", "F verso l'alto"].concat(
      cont1 ? ["elemento continuo: nessuna quota di estremita"] : [],
    ),
  });
  const v2 = plan({
    width: state.b2,
    endV: -state.d2,
    pts: pts2,
    extra: ptsB2,
    continuous: cont2,
    layer: "ELEMENTO_2",
    title: three ? "ELEMENTI 2 E 3 (identici)" : steel3 ? "ELEMENTO 2 (legno)" : "ELEMENTO 2",
    sub: ["vista nel proprio piano", "estremita inferiore in basso"].concat(
      cont2 ? ["elemento continuo: nessuna quota di estremita"] : [],
    ),
  });
  const gapV = 3 * th,
    pad = 2 * th;
  // left column: front view; right column: frame with the single elements and the edge-distance table; below, full
  // width, the title block
  const ox0 = v0.left,
    oy0 = -v0.up;
  v0.draw(ox0, oy0);
  const xF = ox0 + v0.right + 2 * gapV;
  const ox1 = xF + pad + v1.left,
    ox2 = ox1 + v1.right + gapV + v2.left;
  const oy1 = -v1.up,
    oy2 = -v2.up;
  v1.draw(ox1, oy1);
  v2.draw(ox2, oy2);
  const drawInfo = (xL: number, yStart: number) => {
    // title block
    let y = yStart;
    const plain = (s: unknown) => String(s == null ? "" : s).replace(/<[^>]*>/g, "");
    const dataFmt = formatReportDate(ctx.meta);
    const sc = state.screwRec;
    const matLabel = (n: 1 | 2) => {
      const clt = n === 1 ? state.clt1 : state.clt2;
      if (clt) return `Pannello CLT, ${clt.numStrati} strati, lato ${clt.latoInserimento}, classe ${clt.classe}`;
      const m = ctx.materials[n];
      if (/^USER\b/.test(m.tipo)) return m.customName ? `${m.tipo} - ${m.customName}` : m.tipo;
      return `${m.tipo} ${m.specie} ${m.classe}`.replace(/\s+/g, " ").trim();
    };
    const t1 = state.t1,
      t2 = state.t2;
    const info: [string, string][] = [
      [
        "PROGETTO",
        [(ctx.meta.projectName || "").trim() || "Progetto senza titolo", ctx.meta.calculationName, dataFmt]
          .filter(Boolean)
          .join(" - "),
      ],
      ["CONNESSIONE", `${connectionTitle(ctx.connectionType, ctx)} - angolo theta = ${fmtN(state.theta, 0)} gradi`],
      [
        steel3 ? "ELEMENTI 1 E 3" : "ELEMENTO 1",
        `${plain(matLabel(1))} - b1 = ${posFmt(state.b1)} mm, d1 = ${posFmt(state.d1)} mm, t1 = ${fmtN(t1, 1)} mm${three ? " (centrale)" : ""}${steel3 ? (state.panel1 ? " (pannelli identici)" : " (lamiere identiche)") : ""}`,
      ],
      [
        three ? "ELEMENTI 2 E 3" : "ELEMENTO 2",
        `${plain(matLabel(2))} - b2 = ${posFmt(state.b2)} mm, d2 = ${posFmt(state.d2)} mm, t2 = ${fmtN(t2, 1)} mm${three ? " (identici)" : ""}${steel3 ? " (legno centrale)" : ""}`,
      ],
      [
        "VITE",
        sc
          ? `${sc.family} - ${plain(sc.tipoVite || sc.descrizioneCatalogo || "")} (SKU ${sc.sku}) - d = ${fmtN(d, 1)} mm, L = ${fmtN(state.screwL, 0)} mm`
          : `d = ${fmtN(d, 1)} mm`,
      ],
      [
        "POSA",
        `${(three && !state.threeD) || steel3 ? `${grid.length} viti per lato (totale ${2 * grid.length}, griglia speculare sui due lati)` : `${grid.length} viti${state.threeD ? " (ciascuna attraversa i tre elementi)" : ""}`} (griglia ${state.rows} x ${state.cols}${state.sfalsaViti ? ", sfalsate" : ", allineate"}) - passi s1 = ${posFmt(state.s11)} mm, s2 = ${posFmt(state.s12)} mm - ${state.predrilled ? "con preforo" : "senza preforo"}${state.rondellaInfo ? ` - rondella ${state.rondellaSelCodice} (h = ${fmtN(state.rondellaH, 1)} mm)` : ""}`,
      ],
      [
        "VERIFICA",
        `Fv,Ed = ${fmtN(state.forceF_kN, 2)} kN - Fv,Rd = ${fmtN(state.FvRdUnione / 1000, 2)} kN${three || steel3 ? (state.threeD ? " (taglio doppio, 2 piani di taglio)" : " (2 connessioni a taglio singolo)") : ""} - eta = ${fmtN(state.eta * 100, 0)} %${state.etaCombinata != null && state.faKN > 0 ? ` - carico combinato eta = ${fmtN(state.etaCombinata * 100, 0)} %` : ""}${(three || steel3) && state.etaSplitting != null ? ` - spacco elemento ${steel3 ? 2 : 1} eta = ${fmtN(state.etaSplitting * 100, 0)} %` : ""} - VERIFICATO (ETA-11/0030, EN 1995-1-1)`,
      ],
    ];
    if (state.threeD)
      info.splice(6, 0, [
        "PENETRAZIONE",
        `la vite attraversa gli elementi 2 e 1 (t2 + t1 = ${fmtN(state.tHead, 0)} mm) ed entra di ${fmtN((state.screwLEff ?? NaN) - state.tHead, 0)} mm nell'elemento 3 (L-h = ${fmtN(state.screwLEff, 0)} mm), taglio doppio EC5 eq. 8.7`,
      ]);
    if (state.spreadActive)
      info.splice(6, 0, [
        "DISTRIBUZIONE",
        `viti distribuite nella zona ammissibile: s1 = ${posFmt(state.s11)} mm, s2 = ${posFmt(state.s12)} mm (minimi richiesti ${posFmt(state.s11Min)} / ${posFmt(state.s12Min)} mm)`,
      ]);
    if ((three || steel3) && state.stagger)
      info.splice(6, 0, [
        "SFALSAMENTO",
        `viti del lato 3 sfalsate di 1 x d = ${fmtN(d, 1)} mm (direzione ${state.staggerDir}): L-h = ${fmtN(state.screwLEff, 0)} mm supera ${steel3 ? "t1 + t2/2" : "t2 + t1/2"} = ${fmtN(state.tHead + state.tPoint / 2, 0)} mm`,
      ]);
    const ti = th * 0.85;
    info.forEach(([k, v]) => {
      D.text(D.E, "TESTI", xL, y, ti, k);
      D.text(D.E, "TESTI", xL + 16 * ti, y, ti, v);
      y -= 1.9 * ti;
    });
    y -= 1.5 * th;
    return y;
  };
  const drawTable = (xL: number, y: number) => {
    // position table (as in the report: up to 40 screws listed, otherwise minimum/maximum)
    const tt = th * 0.8,
      rh = 2 * tt,
      cw = 9.5 * tt,
      c0 = 5 * tt;
    const hline = (yy: number, x1: number, x2: number) => D.line(D.E, "TABELLA", x1, yy, x2, yy);
    const vline = (xx: number, y1: number, y2: number) => D.line(D.E, "TABELLA", xx, y1, xx, y2);
    const cell = (xc: number, yy: number, str: string, o?: TextOpts) =>
      D.text(D.E, "TESTI", xc, yy - rh / 2, tt, str, Object.assign({ align: "c", valign: "m" } as TextOpts, o || {}));
    let yb: number;
    if (grid.length <= 40) {
      const xs: number[] = [xL, xL + c0];
      for (let i = 0; i < 6; i++) xs.push(xs[xs.length - 1]! + cw);
      const X = (i: number) => xs[i]!;
      const rows = grid.length;
      yb = y - (2 + rows) * rh;
      for (let i = 0; i <= 2 + rows; i++) {
        if (i === 1) hline(y - rh, X(1), X(7));
        else hline(y - i * rh, X(0), X(7));
      }
      xs.forEach((x, i) => {
        if (i === 0 || i === 1 || i === 4 || i === 7) vline(x, y, yb);
        else vline(x, y - rh, yb);
      });
      cell((X(0) + X(1)) / 2, y - rh / 2, "N.", { valign: "m" });
      cell((X(1) + X(4)) / 2, y, "ELEMENTO 1 [mm]");
      cell((X(4) + X(7)) / 2, y, three ? "ELEMENTI 2-3 [mm]" : "ELEMENTO 2 [mm]");
      ["estremita", "sinistro", "destro", "sinistro", "destro", "estremita"].forEach((t, i) =>
        cell((X(1 + i) + X(2 + i)) / 2, y - rh, t),
      );
      grid.forEach((p, i) => {
        const yy = y - (2 + i) * rh;
        const vals = [
          i + 1,
          cont1 ? "-" : posFmt(p.u + state.d1),
          posFmt(p.v + state.b1 / 2),
          posFmt(state.b1 / 2 - p.v),
          posFmt(p.x + state.b2 / 2),
          posFmt(state.b2 / 2 - p.x),
          cont2 ? "-" : posFmt(p.y + state.d2),
        ];
        vals.forEach((v, j) => cell((X(j) + X(j + 1)) / 2, yy, String(v)));
      });
    } else {
      const mm = (fn: (p: (typeof grid)[number]) => number): [number, number] => [
        Math.min(...grid.map(fn)),
        Math.max(...grid.map(fn)),
      ];
      const rowsT: [string, [number, number]][] = [];
      if (!cont1) rowsT.push(["Elemento 1 - dall'estremita", mm((p) => p.u + state.d1)]);
      rowsT.push(["Elemento 1 - dal bordo sinistro", mm((p) => p.v + state.b1 / 2)]);
      rowsT.push([
        three ? "Elementi 2 e 3 - dal bordo sinistro" : "Elemento 2 - dal bordo sinistro",
        mm((p) => p.x + state.b2 / 2),
      ]);
      if (!cont2)
        rowsT.push([
          three ? "Elementi 2 e 3 - dall'estremita inferiore" : "Elemento 2 - dall'estremita inferiore",
          mm((p) => p.y + state.d2),
        ]);
      const xs = [xL, xL + 38 * tt, xL + 38 * tt + cw, xL + 38 * tt + 2 * cw] as const;
      yb = y - (1 + rowsT.length) * rh;
      for (let i = 0; i <= 1 + rowsT.length; i++) hline(y - i * rh, xs[0], xs[3]);
      xs.forEach((x) => vline(x, y, yb));
      cell(xs[0] + 1 * tt, y, "Distanza dal bordo [mm]", { align: "l" });
      cell((xs[1] + xs[2]) / 2, y, "minima");
      cell((xs[2] + xs[3]) / 2, y, "massima");
      rowsT.forEach((rw, i) => {
        const yy = y - (1 + i) * rh;
        cell(xs[0] + 1 * tt, yy, rw[0], { align: "l" });
        cell((xs[1] + xs[2]) / 2, yy, posFmt(rw[1][0]));
        cell((xs[2] + xs[3]) / 2, yy, posFmt(rw[1][1]));
      });
      D.text(
        D.E,
        "TESTI",
        xL,
        yb - 1.4 * tt,
        tt * 0.9,
        "Con piu di 40 viti non si elencano le coordinate di ogni vite: valgono le quote d'insieme e i passi s1/s2 della griglia.",
      );
      yb -= 2.4 * tt;
    }
    return { yb, w: grid.length <= 40 ? c0 + 6 * cw : 38 * tt + 2 * cw };
  };
  const drawFoot = (xL: number, yb: number) => {
    const nFuori = state.grid.length - grid.length;
    D.text(
      D.E,
      "TESTI",
      xL,
      yb - 2 * th,
      th * 0.7,
      `Tabella: distanze in mm dal bordo dell'elemento all'asse della vite. Viti disegnate con diametro reale (croce = asse).${three && !state.threeD ? " Elementi 2 e 3 identici: le viti del lato 3 sono la copia speculare di quelle del lato 2 (tabella riferita al lato 2)." : ""}${state.threeD ? " Elementi 2 e 3 identici: la stessa vite attraversa i tre elementi." : ""}${steel3 ? (state.panel1 ? " Pannelli" : " Lamiere") + " 1 e 3 identic" + (state.panel1 ? "i" : "he") + ": le viti del lato 3 sono la copia speculare di quelle del lato 1 (tabella riferita al lato 1)." : ""}${nFuori ? ` ${nFuori} viti fuori inviluppo escluse (non da posare).` : ""} Generato da MY PROJECT - Connessioni viti a taglio il ${new Date().toLocaleDateString("it-IT")}.`,
    );
  };
  const tb = drawTable(xF + pad, Math.min(oy1 + v1.down, oy2 + v2.down) - 3 * th);
  const xFR = Math.max(ox2 + v2.right, xF + pad + tb.w) + pad;
  D.polyline(
    D.E,
    "TABELLA",
    [
      [xF, pad],
      [xFR, pad],
      [xFR, tb.yb - pad],
      [xF, tb.yb - pad],
    ],
    true,
  );
  const yAfter = drawInfo(0, Math.min(oy0 + v0.down, tb.yb - pad) - 4 * th);
  drawFoot(0, yAfter);
  return D.finish([-2 * th, yAfter - 6 * th, xFR, pad]);
}

// Builds the DXF and downloads it ("Dettaglio_viti_<progetto>.dxf"); no-op when the export is not available.
export function exportDxf(state: CalcState | null, ctx: ReportContext, blockReason?: string | null): void {
  if (!state || !dxfAvailability(state, blockReason).ok) return;
  const txt = buildDxf(state, ctx);
  const nome =
    [ctx.meta.projectName, ctx.meta.calculationName]
      .filter(Boolean)
      .join("_")
      .trim()
      .replace(/[^A-Za-z0-9_-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "connessione";
  const blob = new Blob([txt], { type: "application/dxf" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Dettaglio_viti_${nome}.dxf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
